(() => {
  if (window.leavysDiscoveryEvents) return;
  const eventNames = new Set(['filter_open','filter_apply','filter_remove','sort_change','shortcut_select','page_change','page_size_change','empty_results','select_item','search_submit','search_results','search_empty','search_click','search_help']);
  const events = [];
  const measurementId = 'G-5VGX1MYPG2';
  const queueKey = 'leavys_discovery_queue_v2';
  const testKey = 'leavys_tracking_test_v2';
  const consented = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const storage = {
    get(key, fallback) {try {return JSON.parse(sessionStorage.getItem(key)) ?? fallback;} catch {return fallback;}},
    set(key, value) {try {sessionStorage.setItem(key, JSON.stringify(value));} catch {}},
    remove(key) {try {sessionStorage.removeItem(key);} catch {}}
  };
  let pending = [];
  let flushing = false;
  let lastResults = '';
  let ready = false;
  let testMode = false;
  window.leavysDiscoveryEvents = {events, version:'2026-10-20'};
  function diagnostic(stage) {
    if (testMode || new URL(location.href).searchParams.get('ls_tracking_test') === '1') {
      document.documentElement.dataset.lsTrackingStatus = JSON.stringify({stage,consent:consented(),googleTag:typeof window.gtag,queued:pending.length,sent:pending.filter(row=>row.sent).length,mode:testMode?'test':'live'});
    }
  }
  function cleanTerm(value) {
    const term = String(value || '').normalize('NFKC').trim().replace(/\s+/g,' ').toLowerCase();
    // Never retain contact details, links or long numbers entered into search.
    if (/@|https?:|www\.|\b(?:\d[\s()+.-]*){7,}\b/.test(term)) return '[redacted]';
    return term.slice(0,100);
  }
  function searchContext(form, target) {
    const url = target || new URL(location.href);
    let config = {};
    try {config = JSON.parse(document.querySelector('[data-leavys-search-config]')?.textContent || '{}');} catch {}
    const selected = url.searchParams.getAll('filter.v.t.shopify.target-gender');
    const departments = Object.entries(config.departments || {}).filter(([,value])=>selected.includes(value)).map(([key])=>key);
    const scope = target && departments.length === 1 ? departments[0] : (form?.dataset.lsScope || (departments.length === 1 ? departments[0] : ''));
    return {
      search_term:cleanTerm(form?.querySelector('[data-search-input]')?.value || url.searchParams.get('lsq') || url.searchParams.get('q')),
      ls_search_department:/^wom/i.test(scope)?'women':/^men/i.test(scope)?'men':/^unisex$/i.test(scope)?'unisex':'unspecified'
    };
  }
  function start() {
    diagnostic('starting');
    if (!consented()) return;
    if (!ready) {
      ready = true;
      const flag = new URL(location.href).searchParams.get('ls_tracking_test');
      if (flag === '0') storage.remove(testKey);
      if (flag === '1') storage.set(testKey,true);
      testMode = storage.get(testKey,false) === true;
      pending = storage.get(queueKey,[]).filter(row=>row.created > Date.now()-120000).slice(-20);
    }
    flush();
    recordResults('page');
  }
  function googleIdentity() {
    return new Promise(resolve => {
      if (typeof window.gtag !== 'function') return resolve(null);
      const identity = {};
      const timer = setTimeout(()=>resolve(null),1000);
      const done = () => {
        if (!identity.client_id || !identity.session_id) return;
        clearTimeout(timer);
        resolve(identity);
      };
      window.gtag('get',measurementId,'client_id',value=>{identity.client_id=value;done();});
      window.gtag('get',measurementId,'session_id',value=>{identity.session_id=value;done();});
    });
  }
  async function flush() {
    if (flushing || !consented() || !pending.length) return;
    flushing = true;
    try {
      const identity = await googleIdentity();
      diagnostic(identity?'identity-ready':'waiting-google');
      if (!identity || !consented()) return;
      // Retain the small outbox for replay on the destination page. The pixel
      // deduplicates the ID, including when navigation interrupts delivery.
      for (const row of pending.filter(row=>row.created > Date.now()-120000 && !row.sent)) {
        if (!consented()) break;
        if (typeof window.Shopify?.analytics?.publish !== 'function') break;
        await window.Shopify.analytics.publish(`leavys:${row.name}`,{...row.data,ga_client_id:String(identity.client_id),ga_session_id:String(identity.session_id),tracking_event_id:row.id});
        row.sent = true;
        diagnostic('published');
      }
    } catch {} finally {flushing = false;}
  }
  function context() {
    const root = document.querySelector('leavys-results, [data-mens-sale]');
    let config = {};
    try {config = JSON.parse(document.querySelector('[data-leavys-search-config]')?.textContent || '{}');} catch {}
    if (!root && config.preview === undefined) return null;
    const url = new URL(location.href);
    const filters = [...url.searchParams.keys()].filter(key => key.startsWith('filter.') && key !== 'filter.v.availability' || ['uk_size','sale_brand','sale_style','sale_fit'].includes(key));
    const names = {uk_size:'size',sale_brand:'brand',sale_style:'style',sale_fit:'width','filter.v.option.size':'size','filter.v.t.shopify.shoe-fit':'width','filter.p.tag':'style','filter.v.t.shopify.target-gender':'department','filter.v.t.shopify.color-pattern':'colour','filter.p.vendor':'brand','filter.v.price.gte':'price','filter.v.price.lte':'price','filter.v.t.shopify.occasion-style':'occasion','filter.v.t.shopify.closure-type':'closure','filter.v.t.shopify.heel-height-type':'heel_height'};
    return {
      environment: (root ? root.dataset.nfPreview !== 'false' : config.preview) ? 'preview' : 'live',
      item_list_id: root?.dataset.nfListId || 'search_suggestions',
      item_list_name: root?.dataset.nfListName || 'Search suggestions',
      result_count: Number(root?.dataset.nfResultCount || 0),
      result_page: Number(root?.dataset.nfPage || 1),
      filter_count: new Set(filters).size + (url.searchParams.has('ls_style') ? 1 : 0),
      filter_types: [...new Set(filters.map(key=>names[key]).filter(Boolean).concat(url.searchParams.has('ls_style') ? ['style'] : []))].sort().join('|'),
      selected_sizes: url.searchParams.getAll(root?.matches('[data-mens-sale]') ? 'uk_size' : 'filter.v.option.size').filter(value=>/^\d{1,2}(?:\.5)?$/.test(value)).slice(0,10).join('|'),
      wide_fit_active: (url.searchParams.getAll('filter.v.t.shopify.shoe-fit').includes('gid://shopify/TaxonomyValue/17304') || url.searchParams.get('sale_fit') === 'Wide') ? 1 : 0,
      size_count: url.searchParams.getAll(root?.matches('[data-mens-sale]') ? 'uk_size' : 'filter.v.option.size').length,
      sort_order: root?.querySelector('[name="sort_by"], [data-sale-sort]')?.value || '',
      page_path: location.pathname
    };
  }
  function emit(name, properties = {}) {
    if (!eventNames.has(name)) return;
    const common = context();
    if (!common) return;
    const payload = {...common,...properties,ls_tracking_mode:testMode?'test':'live'};
    if (common.environment === 'preview') {
      events.push({name,...payload});
      if (events.length > 50) events.shift();
      return;
    }
    if (!consented()) return;
    const row = {id:window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,created:Date.now(),name,data:payload};
    pending = pending.filter(row=>row.created > Date.now()-120000).concat(row).slice(-20);
    storage.set(queueKey,pending.map(({sent,...entry})=>entry));
    flush();
  }
  function recordResults(source) {
    const root = document.querySelector('leavys-results');
    if (location.pathname !== '/search' || !root || !root.hasAttribute('data-nf-result-count')) return;
    if (!consented() && context()?.environment !== 'preview') return;
    const signature = location.href + '|' + root.dataset.nfResultCount;
    if (signature === lastResults) return;
    lastResults = signature;
    const properties = {...searchContext(),ls_search_source:source};
    emit('search_results',properties);
    if (Number(root.dataset.nfResultCount) === 0) emit('search_empty',properties);
  }
  document.addEventListener('submit', e => {
    const form = e.target.closest('[data-ls-form]');
    if (!form || !form.querySelector('[data-search-input]')?.value.trim()) return;
    emit('search_submit',{...searchContext(form),ls_search_source:'form'});
  },{capture:true});
  document.addEventListener('click', e => {
    const link = e.target.closest('.ls-all-results, .ls-scope-links a, .ls-help-link');
    if (!link) return;
    const target = new URL(link.href,location.href);
    emit(link.matches('.ls-help-link')?'search_help':'search_submit',{
      ...searchContext(link.closest('[data-ls-form]'),target),
      ls_search_source:link.matches('.ls-help-link')?'help':link.matches('.ls-all-results')?'view_all':'department'
    });
  },{capture:true});
  document.addEventListener('leavys:filters-rendered',()=>recordResults('refinement'));
  document.addEventListener('leavys:discovery', e => {
    emit(e.detail?.name, e.detail?.source ? {interaction_source:e.detail.source} : {});
  });
  function selectItem(e) {
    if (e.type === 'click' && e.button !== 0 || e.type === 'auxclick' && e.button !== 1) return;
    const link = e.target.closest('a[href]');
    const card = link?.closest('.ls-product-link[data-product-id], leavys-results [data-product-id], [data-mens-sale] [data-product-id]');
    if (!card) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
    const variant = url.searchParams.get('variant') || card.dataset.nfVariant;
    if (card.matches('.ls-product-link') || location.pathname === '/search') {
      emit('search_click',{
        ...searchContext(card.closest('[data-ls-form]')),
        ls_search_source:card.matches('.ls-product-link')?'suggestion':'results',
        product_id:card.dataset.productId,
        product_position:[...card.parentElement.querySelectorAll('[data-product-id]')].indexOf(card)+1
      });
    }
    emit('select_item', {
      ...(card.matches('.ls-product-link') ? {item_list_id:'search_suggestions',item_list_name:'Search suggestions'} : {}),
      currency: card.dataset.nfCurrency,
      interaction_source: link.closest('.ls-product-link') ? 'search_suggestion' : link.closest('.nf-card__sizes') ? 'size_link' : link.closest('.nf-card__image') ? 'image' : 'title',
      items: [{
        item_id: `shopify_${card.dataset.nfCountry}_${card.dataset.productId}_${variant}`,
        item_name: card.dataset.nfItemName,
        item_brand: card.dataset.nfBrand,
        item_variant: link.dataset.nfVariantName || card.dataset.nfVariantName,
        index: [...card.parentElement.querySelectorAll('[data-product-id]')].indexOf(card),
        price: Number(link.dataset.nfPrice || card.dataset.nfPrice)
      }]
    });
  }
  document.addEventListener('click',selectItem,{capture:true});
  document.addEventListener('auxclick',selectItem,{capture:true});
  if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}],start);
  }
  document.addEventListener('visitorConsentCollected',()=>{
    if (consented()) start();
    else {pending=[];ready=false;lastResults='';storage.remove(queueKey);storage.remove(testKey);}
  });
  window.addEventListener('pageshow',start);
  start();
  // Only a short retry window is needed while the existing Google tag loads.
  let attempts = 0;
  const retry = setInterval(()=>{start();if (++attempts >= 15) clearInterval(retry);},1000);
})();
